import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext.tsx';
import { useNavigate } from 'react-router-dom';
import AlumniSidebar from '../family/AlumniSidebar.tsx';
import AlumniProfile from '../family/AlumniProfile.tsx';
import NotificationsList from '../family/NotificationsList.tsx';
import EventCalendar from '../family/EventCalendar.tsx';
import DocumentsView from '../family/DocumentsView.tsx';
import HorsesView from '../family/HorsesView.tsx';
import DirectoryView from '../family/DirectoryView.tsx';
import AlumniInvoices from '../family/AlumniInvoices.tsx';
import GirlsCornerView from '../family/GirlsCornerView.tsx';
import { AppIcon, AppIconName } from '../../icons/appIcons.tsx';

// Helper function to format date string without timezone conversion
const formatDateSafe = (dateStr: string) => {
  const [year, month, day] = dateStr.split('-');
  return `${month}/${day}/${year}`;
};

interface Event {
  id: string;
  title: string;
  description: string;
  date: string;
  time: string;
  location: string;
  maxAttendees: number;
  currentAttendees: number;
  rsvpEnabled: boolean;
  rsvpDeadline?: string;
  createdBy: string;
  createdAt: string;
  rsvps: any[];
}

interface AlumniData {
  id: string;
  firstName: string;
  lastName: string;
  memberType: 'alumni' | 'responsible_adult';
  category: string;
  email: string;
  phone: string;
  isActingBigSister: boolean;
  isActive: boolean;
  notes?: string;
  // Membership requirements
  hasPaperwork: boolean;
  hasBackgroundCheck: boolean;
  hasPaidDues: boolean;
  isApproved: boolean;
  isAlsoResponsibleAdult?: boolean;
  isAlsoAlumni?: boolean;
}

export default function AlumniDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('dashboard');
  const [alumniData, setAlumniData] = useState<AlumniData | null>(null);
  const [loading, setLoading] = useState(true);
  const [upcomingEvents, setUpcomingEvents] = useState<Event[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);

  useEffect(() => {
    // Stagger API calls to prevent rate limiting
    fetchAlumniData();
    
    // Delay events fetch by 500ms
    setTimeout(() => {
      fetchUpcomingEvents();
    }, 500);
  }, []);

  const fetchAlumniData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/alumni/profile', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 429) {
        console.warn('Rate limited - delaying alumni data fetch');
        setLoading(false);
        return;
      }

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Failed to parse alumni data response:', parseError);
        setLoading(false);
        return;
      }

      if (data.success) {
        setAlumniData(data.data);
      }
    } catch (error) {
      console.error('Error fetching alumni data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcomingEvents = async () => {
    try {
      setEventsLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/events?upcoming=true', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 429) {
        console.warn('Rate limited - delaying events fetch');
        setEventsLoading(false);
        return;
      }

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Failed to parse events response:', parseError);
        setEventsLoading(false);
        return;
      }

      if (data.success) {
        setUpcomingEvents(data.data);
      }
    } catch (error) {
      console.error('Error fetching upcoming events:', error);
    } finally {
      setEventsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const renderContent = () => {
    switch (activeView) {
      case 'events':
        return <EventCalendar />;
      case 'horses':
        return <HorsesView />;
      case 'profile':
        return <AlumniProfile />;
      case 'documents':
        return <DocumentsView />;
      case 'directory':
        return <DirectoryView />;
      case 'girlscorner':
        return <GirlsCornerView />;
      case 'notifications':
        return <NotificationsList />;
      case 'invoices':
        return <AlumniInvoices />;
      default:
        // Dashboard home view
        // Calculate missing requirements
        const missingRequirements: { id: string; icon: AppIconName; title: string; message: string; color: string }[] = [];
        if (alumniData && !alumniData.hasPaperwork) {
          missingRequirements.push({
            id: 'paperwork',
            icon: 'clipboard',
            title: 'Paperwork Needed',
            message: 'Please submit your required paperwork to complete your membership.',
            color: 'amber'
          });
        }
        if (alumniData && !alumniData.hasBackgroundCheck) {
          missingRequirements.push({
            id: 'background',
            icon: 'search',
            title: 'Background Check Required',
            message: 'A background check is required for all members. Please contact us to get started.',
            color: 'orange'
          });
        }
        if (alumniData && !alumniData.hasPaidDues) {
          missingRequirements.push({
            id: 'dues',
            icon: 'payment',
            title: 'Dues Payment Due',
            message: 'Your annual dues are pending. Please check your invoices to complete payment.',
            color: 'rose'
          });
        }

        return (
          <div className="px-4 sm:px-6 lg:px-12 py-4 sm:py-6 lg:py-10">
            {/* Welcome Header */}
            <div className="mb-6 sm:mb-8">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
                Welcome, {alumniData?.firstName} {alumniData?.lastName}!
              </h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">
                {alumniData?.category === 'ranch-girl' && 'Alumni Ranch Girl'}
                {alumniData?.category === 'alumni-mom' && 'Alumni Mom'}
                {alumniData?.category === 'alumni-dad' && 'Alumni Dad'}
                {alumniData?.isActingBigSister && ' • Big Sister'}
              </p>
            </div>

            {/* Membership Requirements Alert Banner */}
            {missingRequirements.length > 0 && (
              <div className="mb-6 sm:mb-8">
                <div className="bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 rounded-2xl shadow-xl border-2 border-amber-300 overflow-hidden">
                  {/* Header */}
                  <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 sm:px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-full flex items-center justify-center mr-3 sm:mr-4">
                        <AppIcon name="info" className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                      </div>
                      <div>
                        <h2 className="text-lg sm:text-xl font-bold">Action Required</h2>
                        <p className="text-amber-100 text-xs sm:text-sm">
                          Please complete the following to maintain your active membership
                        </p>
                      </div>
                      <div className="ml-auto bg-white/20 px-3 py-1 rounded-full">
                        <span className="text-sm font-semibold">{missingRequirements.length} item{missingRequirements.length > 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  </div>
                  {/* Requirement Cards */}
                  <div className="p-4 sm:p-6">
                    <div className={`grid gap-4 ${missingRequirements.length === 1 ? 'grid-cols-1' : missingRequirements.length === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
                      {missingRequirements.map((req) => (
                        <div
                          key={req.id}
                          className={`relative overflow-hidden rounded-xl p-4 sm:p-5 ${
                            req.color === 'amber' ? 'bg-gradient-to-br from-amber-100 to-amber-50 border-2 border-amber-300' :
                            req.color === 'orange' ? 'bg-gradient-to-br from-orange-100 to-orange-50 border-2 border-orange-300' :
                            'bg-gradient-to-br from-rose-100 to-rose-50 border-2 border-rose-300'
                          }`}
                        >
                          {/* Decorative background icon */}
                          <div className="absolute -right-4 -bottom-4 opacity-10">
                            <AppIcon name={req.icon} className="w-24 h-24 sm:w-28 sm:h-28" />
                          </div>
                          <div className="relative z-10">
                            <div className="flex items-start gap-3">
                              <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center ${
                                req.color === 'amber' ? 'bg-amber-200' :
                                req.color === 'orange' ? 'bg-orange-200' :
                                'bg-rose-200'
                              }`}>
                                <AppIcon name={req.icon} className="w-6 h-6 sm:w-7 sm:h-7" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className={`font-bold text-sm sm:text-base ${
                                  req.color === 'amber' ? 'text-amber-800' :
                                  req.color === 'orange' ? 'text-orange-800' :
                                  'text-rose-800'
                                }`}>
                                  {req.title}
                                </h3>
                                <p className={`mt-1 text-xs sm:text-sm ${
                                  req.color === 'amber' ? 'text-amber-700' :
                                  req.color === 'orange' ? 'text-orange-700' :
                                  'text-rose-700'
                                }`}>
                                  {req.message}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Helpful note */}
                    <div className="mt-4 pt-4 border-t border-amber-200">
                      <p className="text-center text-xs sm:text-sm text-amber-700">
                        <span className="font-medium">Questions?</span> Contact us and we'll be happy to help!
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Notifications Banner */}
            <div className="mb-6 sm:mb-8">
              <div className="bg-gradient-to-r from-ggaa-green/5 to-ggaa-cream/20 rounded-xl shadow-lg border-2 border-ggaa-green/30 overflow-hidden">
                <div className="bg-ggaa-green text-white px-4 sm:px-6 py-3">
                  <div className="flex items-center">
                    <AppIcon name="notifications" className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 shrink-0" />
                    <h2 className="text-base sm:text-lg font-semibold">Ranch Notifications</h2>
                    <button
                      onClick={() => setActiveView('notifications')}
                      className="ml-auto text-xs sm:text-sm hover:underline"
                    >
                      View All →
                    </button>
                  </div>
                </div>
                <div className="p-4 sm:p-6">
                  <NotificationsList 
                    compact={true} 
                    maxItems={3}
                    onViewAll={() => setActiveView('notifications')}
                  />
                </div>
              </div>
            </div>

            {/* Girls' Corner Section */}
            <div className="mb-6 sm:mb-8">
              <GirlsCornerView compact={true} />
            </div>

            {/* Main Dashboard Content - Three Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {/* Upcoming Events */}
              <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-ggaa-green/20">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900">Upcoming Events</h3>
                  <button
                    onClick={() => setActiveView('events')}
                    className="text-xs sm:text-sm text-ggaa-green hover:underline"
                  >
                    View All
                  </button>
                </div>
                {eventsLoading ? (
                  <div className="flex items-center justify-center py-6 sm:py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ggaa-green"></div>
                    <span className="ml-2 text-sm text-gray-600">Loading...</span>
                  </div>
                ) : upcomingEvents.length === 0 ? (
                  <div className="text-center py-6 sm:py-8 text-gray-500">
                    <p className="text-xs sm:text-sm">No upcoming events</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {upcomingEvents.slice(0, 3).map((event, index) => (
                      <div key={event.id} className={`p-3 rounded-lg ${index === 0 ? 'bg-ggaa-cream' : 'bg-gray-50'}`}>
                        <p className={`font-medium text-sm ${index === 0 ? 'text-ggaa-brown' : 'text-gray-700'} mb-1`}>
                          {event.title}
                        </p>
                        <p className="text-xs text-gray-600">
                          {formatDateSafe(event.date)} at {event.time}
                        </p>
                      </div>
                    ))}
                    {upcomingEvents.length > 3 && (
                      <button
                        onClick={() => setActiveView('events')}
                        className="w-full text-center py-2 text-ggaa-green hover:underline text-xs sm:text-sm"
                      >
                        +{upcomingEvents.length - 3} more events
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Invoices Preview */}
              <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-ggaa-green/20">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900">Invoices</h3>
                  <button
                    onClick={() => setActiveView('invoices')}
                    className="text-xs sm:text-sm text-ggaa-green hover:underline"
                  >
                    View All
                  </button>
                </div>
                <AlumniInvoices compact={true} />
              </div>

              {/* Documents Preview */}
              <div className="md:col-span-2 lg:col-span-1">
                <DocumentsView compact={true} />
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <AlumniSidebar 
        currentView={activeView}
        onNavigate={setActiveView}
        onLogout={handleLogout}
        alumniName={alumniData ? `${alumniData.firstName} ${alumniData.lastName}` : undefined}
      />
      <div className="flex-1 overflow-y-auto pt-16 lg:pt-0">
        {renderContent()}
      </div>
    </div>
  );
}

